import { t } from "@lingui/core/macro";
import { DataTestIds } from "@regrello/data-test-ids-api";
import { useGenerateRegexPatternMutation } from "@regrello/graphql-api";
import { RegrelloSpinner } from "@regrello/ui-core";
import React from "react";
import type { FieldArrayWithId, UseFormReturn } from "react-hook-form";
import { useDebounce } from "react-use";
import { DEBOUNCE_TIMEOUT_LONG, ValidationRules } from "../../../../../../../constants/globalConstants";
import type { ConfigureSpectrumFieldFormFormFields } from "../../../../../../fields/configure/_internal/ConfigureSpectrumFieldForm";
import { RegrelloFormFieldTextMultiline } from "../../../../formFields/RegrelloFormFieldTextMultiline";
import { getValueConstraintDisplayName } from "../../../utils/spectrumFieldConstraintUtils";

interface RegexValidatedConstraintFieldProps {
  /** Index of the constraint within the valueConstraints array */
  constraintIndex: number;
  /** Whether editing is currently disabled. */
  disabled: boolean;
  /** Form for the field in question being updated */
  form: UseFormReturn<ConfigureSpectrumFieldFormFormFields>;
  /** Row for the current field value constraint */
  row: FieldArrayWithId<ConfigureSpectrumFieldFormFormFields, "valueConstraints">;
  /** Used in conjunction with constraintIndex to update the constraint value */
  updateConstraint: (index: number, value: ConfigureSpectrumFieldFormFormFields["valueConstraints"][number]) => void;
}

/**
 * Used for setting a regex field value constraint. Manages the input for the prompt
 * and generation of the regex from the prompt via api. Locks the form and handles race conditions
 * while generation is in progress.
 */
export function RegexValidatedConstraintField(props: RegexValidatedConstraintFieldProps) {
  const { constraintIndex, disabled, row, form, updateConstraint } = props;
  const [generateRegexPatternMutation, { error, loading }] = useGenerateRegexPatternMutation();
  const promptFieldKey = `valueConstraints.${constraintIndex}.args.0` as const;
  const patternFieldKey = `valueConstraints.${constraintIndex}.args.1` as const;
  const valueConstraintKey = `valueConstraints.${constraintIndex}` as const;

  const prompt = form.watch(promptFieldKey);
  const pattern = form.watch(patternFieldKey);

  const hasPrompt = prompt.trim().length > 0;
  const hasPattern = pattern.trim().length > 0;

  // Prevent form submission if there is a prompt with no pattern generated yet (for any reason)
  React.useEffect(() => {
    if (hasPrompt && !hasPattern) {
      form.setError(valueConstraintKey, {
        type: "validate",
        message: "Regex pattern not generated yet",
      });
    } else {
      form.clearErrors(valueConstraintKey);
    }
  }, [form, valueConstraintKey, hasPrompt, hasPattern]);

  const onGenerate = React.useCallback(async () => {
    const promptAtCall = form.getValues(promptFieldKey);
    const hasPromptAtCall = promptAtCall.trim().length > 0;
    if (!hasPromptAtCall) {
      return;
    }

    const resp = await generateRegexPatternMutation({
      variables: {
        input: {
          regexPrompt: promptAtCall,
        },
      },
    });
    if (resp.errors != null) {
      return;
    }

    // Ensure no updates to the prompt since last run. Avoids race conditions
    // and overwriting the constraint to an old value.
    const promptAfterCall = form.getValues(promptFieldKey);
    if (promptAfterCall !== promptAtCall) {
      return;
    }

    const newPattern = resp.data?.generateRegexPattern.pattern;
    if (newPattern == null || newPattern.trim().length === 0) {
      // The backend should alway return a non empty pattern, or an error
      throw new Error("Invariant violated: empty regex pattern from backend");
    }

    const previousValue = form.getValues(valueConstraintKey);
    updateConstraint(constraintIndex, { ...previousValue, args: [promptAtCall, newPattern] });
  }, [constraintIndex, form, generateRegexPatternMutation, updateConstraint, valueConstraintKey, promptFieldKey]);

  const [isReady] = useDebounce(onGenerate, DEBOUNCE_TIMEOUT_LONG, [prompt]);

  const pending = isReady() === false;
  // There is an error if the mutation has returned an error, and
  // 1) prompt is set, if its blank it has been cleared, and we don't care about previous mutation run
  // 2) there is no pending or loading mutation call as that would indicate the user is modifying the
  //    prompt and we likewise don't care about the previous error
  const hasError = error != null && hasPrompt && !pending && !loading;

  const helperText = (() => {
    // 1) If there is an error clear helper text
    if (hasError) {
      return;
    }

    // 2) No Prompt written yet
    if (!hasPrompt) {
      return t`Type a prompt above to generate a regular expression`;
    }

    // 3) Prompt written but pattern not generated yet, show loading state
    if (!hasPattern) {
      return (
        <span>
          {t`Preview: `} <RegrelloSpinner className="align-middle" size="x-small" />
        </span>
      );
    }

    // 4) Both prompt and pattern set, preview the pattern
    if (ValidationRules.REGEX_FN(pattern).isPatternValid()) {
      return t`Preview: ${pattern}`;
    }

    // 5) pattern set but not valid
    return t`Preview: ${pattern}. Warning: this pattern is invalid and will be ignored.`;
  })();

  return (
    <RegrelloFormFieldTextMultiline
      dataTestId={DataTestIds.CREATE_FIELD_DIALOG_FIELD_CONSTRAINT}
      disabled={disabled}
      error={hasError ? t`Unable to generate a valid expression` : undefined}
      helperText={helperText}
      infoTooltipText={t`Type the rules for this field in plain text. This will be used to generate a strict validation rule to ensure users enter the correct data format.\n\nExamples:\n- 'INV-', followed by 6 digits\n- SWIFT or IBAN number\n- Valid VAT number for Germany`}
      label={getValueConstraintDisplayName(row.constraint)}
      minimumRowCount={3}
      onChange={(e) => {
        const previousValue = form.getValues(valueConstraintKey);
        const newPrompt = e.target.value;
        // Set prompt and wipe pattern, as it needs to be regenerated
        updateConstraint(constraintIndex, { ...previousValue, args: [newPrompt, ""] });
      }}
      value={prompt}
    />
  );
}
